// crashBeacon.js -- anonymous crash reports BEFORE analytics consent (spec D9). Single job: post one
// PostHog `$exception` per crash (max 3 per run) with no message text, no persisted or person id, and
// no replay, so onboarding crashes still reach error tracking and the [crash] GitHub alert.
import { randomUUID } from '../../lib/platform';
import { APP_VERSION } from '../../lib/constants';
import * as Sentry from '../../lib/errorReporting';

const ENDPOINT = 'https://us.i.posthog.com/i/v0/e/';
const MAX_PER_RUN = 3;
let sent = 0;
// V8 `at fn (file:l:c)` / `at file:l:c`, and JavaScriptCore (the iOS WebView) `fn@file:l:c` / `@file:l:c`.
// A line matching neither (V8's leading message line) is skipped; JSC stacks have no message line.
const V8_FRAME = /^at (?:(.+?) \()?(.+?):(\d+):(\d+)\)?$/;
const JSC_FRAME = /^(.*?)@(.+?):(\d+):(\d+)$/;

export function parseFrames(stack) {
  return String(stack || '').split('\n').slice(0, 40).map((line) => {
    const m = line.trim().match(V8_FRAME) || line.trim().match(JSC_FRAME);
    if (!m) return null;
    return { function: m[1] || '?', filename: m[2], lineno: Number(m[3]), colno: Number(m[4]), platform: 'web:javascript', in_app: true };
  }).filter(Boolean);
}

const warnFailed = (reason) => Sentry.captureMessage('onboarding: crash beacon send failed', { level: 'warning', tags: { function: 'sendCrash', reason } });

export async function sendCrash(error, { step, fetchImpl = (...a) => fetch(...a), key = process.env.REACT_APP_POSTHOG_KEY, isNative } = {}) {
  try {
    if (!key || !isNative || !isNative() || sent >= MAX_PER_RUN) return;
    sent += 1;
    const body = {
      api_key: key,
      event: '$exception',
      distinct_id: randomUUID(),
      properties: {
        $process_person_profile: false,
        $geoip_disable: true,
        $exception_list: [{ type: (error && error.name) || 'Error', value: '', mechanism: { handled: false, type: 'onboarding-beacon' }, stacktrace: { type: 'raw', frames: parseFrames(error && error.stack) } }],
        app_version: APP_VERSION,
        platform: 'ios',
        onboarding_step: String(step || 'unknown'),
      },
    };
    const res = await fetchImpl(ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), keepalive: true });
    if (!res || !res.ok) warnFailed(`http_${res && res.status}`);
  } catch {
    warnFailed('network');
  }
}
export function resetBeaconForTests() { sent = 0; }
